import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Compute the API base URL.
 *
 * In Emergent's hosting, every domain attached to a deployment (the
 * default `*.emergent.host` URL AND any custom domain like
 * `gaddijunction.in`) is routed by the same ingress: requests prefixed
 * with `/api` go to the FastAPI backend.
 *
 * If the browser is currently on a different origin than the one baked
 * into REACT_APP_BACKEND_URL at build time, we fall back to a
 * *same-origin* relative `/api` path. That eliminates the cross-origin
 * cookie problem entirely (no CORS preflight, no credentials mismatch).
 */
function resolveApiBase() {
  if (typeof window === "undefined" || !BACKEND_URL) {
    return `${BACKEND_URL}/api`;
  }
  try {
    const envOrigin = new URL(BACKEND_URL).origin;
    if (envOrigin !== window.location.origin) {
      return "/api"; // same-origin, no CORS
    }
  } catch (err) {
    console.warn("[gj] REACT_APP_BACKEND_URL is invalid, falling back to /api", err);
    return "/api";
  }
  return `${BACKEND_URL}/api`;
}

export const API = resolveApiBase();

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const INR = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (n >= 10000000) return `₹ ${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹ ${(n / 100000).toFixed(2)} L`;
  return `₹ ${Number(n).toLocaleString("en-IN")}`;
};

export const KM = (n) => `${Number(n).toLocaleString("en-IN")} km`;

export const BRANDS = [
  { name: "Maruti", logo: "https://logos-world.net/wp-content/uploads/2021/03/Maruti-Suzuki-Logo.png" },
  { name: "Hyundai", logo: "https://logos-world.net/wp-content/uploads/2021/03/Hyundai-Logo.png" },
  { name: "Tata", logo: "https://logos-world.net/wp-content/uploads/2021/09/Tata-Logo.png" },
  { name: "Mahindra", logo: "https://logos-world.net/wp-content/uploads/2021/09/Mahindra-Logo.png" },
  { name: "Honda", logo: "https://logos-world.net/wp-content/uploads/2021/03/Honda-Logo.png" },
  { name: "Toyota", logo: "https://logos-world.net/wp-content/uploads/2021/03/Toyota-Logo.png" },
  { name: "Kia", logo: "https://logos-world.net/wp-content/uploads/2021/03/Kia-Logo.png" },
  { name: "Renault", logo: "https://logos-world.net/wp-content/uploads/2021/03/Renault-Logo.png" },
  { name: "BMW", logo: "https://logos-world.net/wp-content/uploads/2020/04/BMW-Logo.png" },
  { name: "Audi", logo: "https://logos-world.net/wp-content/uploads/2020/04/Audi-Logo.png" },
  { name: "Mercedes-Benz", logo: "https://logos-world.net/wp-content/uploads/2020/04/Mercedes-Benz-Logo.png" },
  { name: "Volvo", logo: "https://logos-world.net/wp-content/uploads/2021/03/Volvo-Logo.png" },
  { name: "Skoda", logo: "https://logos-world.net/wp-content/uploads/2021/04/Skoda-Logo.png" },
];

export const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
export const TRANSMISSIONS = ["Manual", "Automatic"];
export const CITIES = ["Kolkata", "Howrah", "Salt Lake", "Durgapur", "Asansol", "Siliguri", "Kharagpur"];

export const waLink = (phone, message) => {
  let clean = (phone || "").replace(/[^0-9]/g, "");
  // Default to Indian dealer support if no phone available
  if (!clean) clean = "918910884194";
  // Ensure Indian 10-digit numbers are prefixed with country code 91
  if (clean.length === 10) clean = `91${clean}`;
  return `https://wa.me/${clean}?text=${encodeURIComponent(message || "Hi, I am interested in this car listed on Gaddi Junction.")}`;
};
